import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  SupportingDocumentItem,
  SupportingDocumentType,
} from "@/features/protest/services/steps.service";
import {
  addLocalSupportingDocuments,
  overwriteLocalSupportingDocumentsFromApi,
  removeLocalSupportingDocument,
} from "@/features/protest/services/localSupportingDocuments.service";
import {
  handleDeleteSupportingDocument,
  handleGetSupportingDocuments,
  handleUploadSupportingDocuments,
} from "@/features/protest/viewModal/stepSupportingDocuments";
import { toastService } from "@/shared/toast";
import styles from "./PropertyStepperScreen.module.css";

export type StepSupportingDocumentsHandle = {
  /**
   * When there are staged files, uploads with description (both required for API).
   * When there are no staged files, skips upload and returns true.
   */
  commitAndCanAdvance: () => Promise<boolean>;
  discardPending: () => void;
};

type StepSupportingDocumentsProps = {
  propertyId: string;
  userId: string;
  documentType: SupportingDocumentType | undefined;
  onDocumentsStateChange?: (state: { hasDocuments: boolean; isLoading: boolean }) => void;
};

const isSympathySavings = (documentType: SupportingDocumentType) =>
  documentType === "SYMPATHY_SAVINGS";
function fileKey(file: File): string {
  return `${file.name}|${file.size}|${file.lastModified}`;
}

function documentLabel(doc: SupportingDocumentItem): string {
  return doc.file_name || doc.name || doc.id;
}

function documentHref(doc: SupportingDocumentItem): string | undefined {
  return doc.document_url || doc.url;
}

function isImageHref(href: string): boolean {
  const lower = href.toLowerCase();
  return (
    lower.endsWith(".png") ||
    lower.endsWith(".jpg") ||
    lower.endsWith(".jpeg") ||
    lower.endsWith(".webp") ||
    lower.endsWith(".gif")
  );
}

function documentKey(doc: SupportingDocumentItem, idx: number): string {
  const href = documentHref(doc) ?? "";
  const name = doc.file_name || doc.name || "";
  const id = doc.id || "";
  return [id, href, name, String(idx)].filter(Boolean).join("|") || `doc-${idx}`;
}

const IMAGE_EXTENSIONS = new Set(["png", "jpg", "jpeg"]);
const DOCUMENT_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "txt",
  "rtf",
  "xls",
  "xlsx",
  "csv",
  "ppt",
  "pptx",
]);

function getFileExtension(fileName: string): string {
  const parts = fileName.toLowerCase().split(".");
  return parts.length > 1 ? parts.pop() || "" : "";
}

/** Prefer the most recently updated row when multiple uploads have descriptions. */
function pickLatestFileDescription(docs: SupportingDocumentItem[]): string {
  if (!docs.length) return "";
  const scored = docs
    .map((d) => {
      const desc = (d.file_description || "").trim();
      const ts = d.updated_at ? Date.parse(d.updated_at) : NaN;
      return { desc, ts: Number.isFinite(ts) ? ts : 0 };
    })
    .filter((x) => x.desc);
  if (!scored.length) return "";
  scored.sort((a, b) => b.ts - a.ts);
  return scored[0]?.desc ?? "";
}

export const StepSupportingDocuments = forwardRef<
  StepSupportingDocumentsHandle,
  StepSupportingDocumentsProps
>(function StepSupportingDocuments(
  { propertyId, userId, documentType, onDocumentsStateChange },
  ref
) {
  const baseId = useId();
  const imagesInputRef = useRef<HTMLInputElement | null>(null);
  const filesInputRef = useRef<HTMLInputElement | null>(null);
  const [documents, setDocuments] = useState<SupportingDocumentItem[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!documentType || !propertyId.trim() || !userId.trim()) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await handleGetSupportingDocuments(userId, documentType, propertyId, {
        bypassCache: true,
      });
      const remoteDocs = Array.isArray(res.data) ? res.data : [];
      if (res.success) {
        overwriteLocalSupportingDocumentsFromApi(propertyId, documentType, remoteDocs);
      }
      const shouldUseRemote = remoteDocs.length > 0;

      if (res.success && shouldUseRemote) {
        setDocuments([...remoteDocs]);
        return;
      }

      if (shouldUseRemote) {
        setError(res.message || null);
        setDocuments([...remoteDocs]);
        return;
      }

      setError(res.success ? null : res.message || "Could not load uploaded documents.");
      setDocuments([]);
    } catch (e) {
      console.error(e);
      setError("Could not load uploaded documents.");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [documentType, propertyId, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPendingFiles([]);
    setDescription("");
    setError(null);
  }, [documentType]);

  const remoteDescriptionHint = useMemo(() => pickLatestFileDescription(documents), [documents]);

  useEffect(() => {
    if (pendingFiles.length > 0) return;
    setDescription(remoteDescriptionHint);
  }, [pendingFiles.length, remoteDescriptionHint]);

  const notifyBusy = useCallback(() => {
    if (!onDocumentsStateChange) return;
    if (!documentType) {
      onDocumentsStateChange({ hasDocuments: false, isLoading: false });
      return;
    }
    const hasDocs = documents.length > 0 || pendingFiles.length > 0;
    const isBusy = loading || uploading;
    onDocumentsStateChange({ hasDocuments: hasDocs, isLoading: isBusy });
  }, [
    documentType,
    documents.length,
    pendingFiles.length,
    loading,
    uploading,
    onDocumentsStateChange,
  ]);

  useEffect(() => {
    notifyBusy();
  }, [notifyBusy]);

  const addPendingFromFileList = (files: FileList | null) => {
    if (!files?.length) return;
    const incoming = Array.from(files);
    setPendingFiles((prev) => {
      const keys = new Set(prev.map(fileKey));
      const merged = [...prev];
      for (const f of incoming) {
        const k = fileKey(f);
        if (!keys.has(k)) {
          keys.add(k);
          merged.push(f);
        }
      }
      return merged;
    });
    setError(null);
  };

  const addPendingImagesFromFileList = (files: FileList | null) => {
    if (!files?.length) return;
    const incoming = Array.from(files);
    const allowed = incoming.filter((file) => IMAGE_EXTENSIONS.has(getFileExtension(file.name)));
    const rejected = incoming.length - allowed.length;
    if (allowed.length > 0) {
      const dt = new DataTransfer();
      for (const file of allowed) dt.items.add(file);
      addPendingFromFileList(dt.files);
    }
    if (rejected > 0) {
      toastService.show("Only image files are allowed (.png, .jpg, .jpeg).", "error");
    }
  };

  const addPendingDocumentsFromFileList = (files: FileList | null) => {
    if (!files?.length) return;
    const incoming = Array.from(files);
    const allowed = incoming.filter((file) => DOCUMENT_EXTENSIONS.has(getFileExtension(file.name)));
    const rejected = incoming.length - allowed.length;
    if (allowed.length > 0) {
      const dt = new DataTransfer();
      for (const file of allowed) dt.items.add(file);
      addPendingFromFileList(dt.files);
    }
    if (rejected > 0) {
      toastService.show(
        "Only document files are allowed (.pdf, .doc, .docx, .txt, .rtf, .xls, .xlsx, .csv, .ppt, .pptx).",
        "error"
      );
    }
  };

  const removePendingAt = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const discardPending = useCallback(() => {
    setPendingFiles([]);
    setDescription(pickLatestFileDescription(documents));
    setError(null);
    if (imagesInputRef.current) imagesInputRef.current.value = "";
    if (filesInputRef.current) filesInputRef.current.value = "";
  }, [documents]);

  const runUpload = useCallback(
    async (files: File[], desc: string): Promise<boolean> => {
      if (!documentType || !propertyId.trim()) return false;
      setUploading(true);
      setError(null);
      try {
        const res = await handleUploadSupportingDocuments(documentType, propertyId, files, desc);
        const message = (res.message || "").toLowerCase();
        const uploadLooksSuccessful = res.success || message.includes("success");
        const description = desc;
        console.log("sadasdsadsadasfdasfsaf", description);
        if (uploadLooksSuccessful) {
          addLocalSupportingDocuments(
            propertyId,
            documentType,
            description,
            files.map((file) => file.name)
          );
          await load();
          return true;
        }
        setError(res.message || "Upload failed.");
        alert(res.message || "Upload failed.");
        toastService.show(res.message || "Upload failed.", "error");
        return false;
      } catch (e) {
        console.error(e);
        setError("Upload failed.");
        toastService.show("Upload failed.", "error");
        return false;
      } finally {
        setUploading(false);
        if (imagesInputRef.current) imagesInputRef.current.value = "";
        if (filesInputRef.current) filesInputRef.current.value = "";
      }
    },
    [documentType, propertyId, load]
  );

  useImperativeHandle(
    ref,
    () => ({
      discardPending,
      commitAndCanAdvance: async () => {
        if (!documentType || !propertyId.trim() || !userId.trim()) {
          return true;
        }

        if (pendingFiles.length === 0 && documentType !== "SYMPATHY_SAVINGS") {
          return true;
        }

        if (!description.trim()) {
          toastService.show("Please enter a description to upload your files.", "error");
          return false;
        }
        const files = [...pendingFiles];
        const desc = description.trim();
        const ok = await runUpload(files, desc);
        if (!ok) return false;
        setPendingFiles([]);
        return true;
      },
    }),
    [description, discardPending, documentType, pendingFiles, propertyId, runUpload, userId]
  );

  console.log(
    "showDescriptionField",
    isSympathySavings(documentType ?? "PROPERTY_WEAR_AND_TEAR"),
    documentType
  );

  const onPickImages = () => imagesInputRef.current?.click();
  const onPickFiles = () => filesInputRef.current?.click();

  const onDelete = async (documentId: string) => {
    if (!window.confirm("Remove this document?")) return;
    setError(null);
    if (documentId.startsWith("local:")) {
      if (!propertyId.trim()) return;
      const localId = documentId.replace("local:", "");
      removeLocalSupportingDocument(propertyId, localId);
      await load();
      return;
    }
    try {
      const res = await handleDeleteSupportingDocument(documentId, {
        propertyIdForCache: propertyId,
      });
      if (res.success) {
        await load();
      } else {
        setError(res.message || "Could not delete document.");
      }
    } catch (e) {
      console.error(e);
      setError("Could not delete document.");
    }
  };

  const onView = (doc: SupportingDocumentItem) => {
    const href = documentHref(doc);
    if (href) {
      window.open(href, "_blank", "noopener,noreferrer");
    } else {
      window.alert("No preview URL is available for this file.");
    }
  };

  if (!documentType) {
    return null;
  }

  if (!propertyId.trim() || !userId.trim()) {
    return (
      <div className={styles.supportingHint} role="status">
        Select a property and sign in to upload supporting documents for this step.
      </div>
    );
  }

  const pickDisabled = uploading;
  const showDescriptionField = pendingFiles.length > 0 || Boolean(remoteDescriptionHint.trim());

  return (
    <div className={styles.supportingSection}>
      <div className={styles.supportingTitle}>Supporting documents</div>
      <p className={styles.supportingSubtitle}>
        Choose files below. When you add files, describe the upload (required). If you already
        uploaded documents, your saved description appears here. Use <strong>Next</strong> to upload
        new files, or <strong>Next</strong> / <strong>Skip</strong> with no new files to continue.
      </p>
      {!isSympathySavings(documentType) && (
        <div className={styles.supportingActions}>
          <input
            ref={imagesInputRef}
            id={`${baseId}-images`}
            type="file"
            className={styles.supportingHiddenInput}
            accept=".png,.jpg,.jpeg"
            multiple
            onChange={(e) => {
              addPendingImagesFromFileList(e.target.files);
              e.target.value = "";
            }}
          />
          <input
            ref={filesInputRef}
            id={`${baseId}-files`}
            type="file"
            className={styles.supportingHiddenInput}
            accept=".pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.csv,.ppt,.pptx"
            multiple
            onChange={(e) => {
              addPendingDocumentsFromFileList(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            className={styles.supportingUploadBtn}
            onClick={onPickImages}
            disabled={pickDisabled}
          >
            {uploading ? "Working…" : "Add images"}
          </button>
          <button
            type="button"
            className={styles.supportingUploadBtnSecondary}
            onClick={onPickFiles}
            disabled={pickDisabled}
          >
            {uploading ? "Working…" : "Add files"}
          </button>
        </div>
      )}

      {isSympathySavings(documentType) || showDescriptionField ? (
        <>
          <label className={styles.supportingFieldLabel} htmlFor={`${baseId}-description`}>
            {pendingFiles.length > 0 ? "Description (required for upload)" : "Description"}
          </label>
          <textarea
            id={`${baseId}-description`}
            className={styles.supportingDescriptionInput}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Briefly describe what you are uploading for this step."
            disabled={pickDisabled}
          />
        </>
      ) : null}

      {!isSympathySavings(documentType) && pendingFiles.length > 0 ? (
        <div className={styles.supportingPendingBlock}>
          <div className={styles.supportingPendingTitle}>Ready to upload (on Next)</div>
          <ul className={styles.supportingPendingList}>
            {pendingFiles.map((file, idx) => (
              <li key={fileKey(file)} className={styles.supportingPendingItem}>
                <span className={styles.supportingFileName}>{file.name}</span>
                <button
                  type="button"
                  className={styles.supportingRemovePendingBtn}
                  onClick={() => removePendingAt(idx)}
                  disabled={pickDisabled}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {!isSympathySavings(documentType) && error ? (
        <div className={styles.supportingError} role="alert">
          {error}
        </div>
      ) : null}

      {!isSympathySavings(documentType) && (
        <div className={styles.supportingList} aria-busy={loading}>
          <div className={styles.supportingSubheading}>Already uploaded</div>
          {loading ? (
            <div className={styles.supportingListEmpty}>Loading uploads…</div>
          ) : documents.length === 0 ? (
            <div className={styles.supportingListEmpty}>No documents uploaded yet.</div>
          ) : (
            <ul className={styles.supportingListUl}>
              {documents.map((doc, idx) => (
                <li key={documentKey(doc, idx)} className={styles.supportingListItem}>
                  <div className={styles.supportingDocMain}>
                    {(() => {
                      const href = documentHref(doc);
                      if (!href || !isImageHref(href)) return null;
                      return (
                        <img
                          className={styles.supportingThumb}
                          src={href}
                          alt={documentLabel(doc)}
                          loading="lazy"
                        />
                      );
                    })()}
                    <span className={styles.supportingFileName}>{documentLabel(doc)}</span>
                  </div>
                  <div className={styles.supportingListActions}>
                    <button
                      type="button"
                      className={styles.supportingLinkBtn}
                      onClick={() => onView(doc)}
                      disabled={!documentHref(doc)}
                    >
                      View
                    </button>
                    <button
                      type="button"
                      className={styles.supportingDeleteBtn}
                      onClick={() => void onDelete(doc.id)}
                      disabled={!doc.id}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
});
